import Modal from '@/fsd/shared/ui/Modal/Modal';
import { observer } from 'mobx-react-lite';
import { mutualSettlementsStore } from '../../model/store/MutualSettlementsStore';
import CustomSelect, {
  Option,
} from '@/fsd/shared/ui/CustomSelect/CustomSelect';
import { Button } from '@/fsd/shared/ui/button';
import { createReconciliation } from '@/fsd/entities/mutualSettlements/api/createReconciliation';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCounterpartiesSelectOptions } from '../../model/hooks/useCounterpartiesSelectOptions';
import { useOrganizationsSelectOptions } from '../../model/hooks/useOrganizationsSelectOptions';
import FormField from '@/fsd/shared/ui/form-field/FormField';
import { formatDateToDDMMYYYY } from '@/fsd/shared/lib/helpers/formatDateToDDMMYYYY/formatDateToDDMMYYYY';
import { DateRangePickerInput } from '../DateRangePickerInput/DateRangePickerInput';
import styles from './CreateReconciliationModal.module.scss';
import { AppRangePicker } from '@fsd/shared/ui/AppDatePicker';
import dayjs from 'dayjs';

const CreateReconciliationModal = observer(() => {
  const queryClient = useQueryClient();

  const { options: counterpartyOptions, isLoading: isCounterpartyLoading } =
    useCounterpartiesSelectOptions();
  const { options: organisationOptions, isLoading: isOrganisationLoading } =
    useOrganizationsSelectOptions();

  const mutation = useMutation({
    mutationFn: createReconciliation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mutualSettlements'] });
      mutualSettlementsStore.setIsCreateModalOpen(false);
      mutualSettlementsStore.reset();
    },
  });

  const handleSubmit = async () => {
    if (
      !mutualSettlementsStore.createForm.counterpartyId ||
      !mutualSettlementsStore.createForm.organisationId
    )
      return;
    await mutation.mutateAsync({
      counterparty_id: mutualSettlementsStore.createForm.counterpartyId,
      organisation_id: mutualSettlementsStore.createForm.organisationId,
      date_from: formatDateToDDMMYYYY(
        mutualSettlementsStore.createForm.dateRange.startDate
      ),
      date_to: formatDateToDDMMYYYY(
        mutualSettlementsStore.createForm.dateRange.endDate
      ),
    });
  };

  const formattedDateRange = `${
    mutualSettlementsStore.createForm.dateRange.startDate?.toLocaleDateString() ||
    '...'
  } - ${
    mutualSettlementsStore.createForm.dateRange.endDate?.toLocaleDateString() ||
    '...'
  }`;

  const isFormValid =
    !!mutualSettlementsStore.createForm.counterpartyId &&
    !!mutualSettlementsStore.createForm.organisationId;

  return (
    <Modal
      className={styles.modal}
      showModal={mutualSettlementsStore.isCreateModalOpen}
      setShowModal={(isOpen: boolean) =>
        mutualSettlementsStore.setIsCreateModalOpen(isOpen)
      }
      showCloseButton
      title="Создание сверки взаиморасчетов"
      footer={
        <div className={styles.footerButtons}>
          <Button
            className={styles.footerButton}
            variant="outline"
            onClick={() => mutualSettlementsStore.setIsCreateModalOpen(false)}
            disabled={mutation.isPending}
          >
            Назад
          </Button>
          <Button
            className={styles.footerButton}
            onClick={handleSubmit}
            disabled={mutation.isPending || !isFormValid}
          >
            {mutation.isPending ? 'Создание...' : 'Создать'}
          </Button>
        </div>
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <FormField label="Период">
          <AppRangePicker
            className={styles.dateInput}
            onChange={(dates) => {
              if (!dates?.[0] || !dates?.[1]) return;

              mutualSettlementsStore.setDateRange({
                startDate: dates[0].toDate(),
                endDate: dates[1].toDate(),
              });
            }}
          />
        </FormField>

        <CustomSelect
          key={mutualSettlementsStore.createForm.counterpartyId}
          title="Контрагент"
          options={counterpartyOptions}
          disabled={isCounterpartyLoading}
          value={mutualSettlementsStore.createForm.counterpartyId}
          onOptionChange={(option: Option) => {
            mutualSettlementsStore.setCounterpartyId(Number(option.value));
          }}
        />
        <CustomSelect
          key={mutualSettlementsStore.createForm.organisationId}
          title="Организация"
          options={organisationOptions}
          disabled={isOrganisationLoading}
          value={mutualSettlementsStore.createForm.organisationId}
          onOptionChange={(option: Option) => {
            mutualSettlementsStore.setOrganisationId(Number(option.value));
          }}
        />
      </div>
    </Modal>
  );
});

export default CreateReconciliationModal;
